import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import {
  ClassVmCostReport,
  ParticipantVmCost,
  ResourceCostItem,
  TemplateVmCost,
} from '../../../../../../../../interfaces/entities/classes-entity-service.interface';
import { ClassEntityService } from '../../../../../../../../services/entity-services/classes-entity.service';
import { ExportService } from '../../../../../../../../services/export.service';
import { DEFAULT_PAGINATOR_PAGE_SIZE, PAGINATOR_PAGE_SIZE_OPTIONS } from '../../../../../../../../shared/pagination-options';

/**
 * Inline standalone dialog shown after the user clicks Refresh 3+ consecutive times.
 * Warns about Azure Cost Management's aggregation delay and offers a persistent suppress option.
 */
@Component({
  selector: 'app-cost-refresh-warning-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatCheckboxModule, MatDialogModule],
  template: `
    <h2 mat-dialog-title>Azure Cost Data Notice</h2>
    <mat-dialog-content>
      <p>
        Azure Cost Management data may take <strong>24–48 hours</strong> to fully aggregate.
        Repeated refreshes will not speed up data availability. Costs shown reflect
        the latest data available from Azure at the time of each request.
      </p>
      <mat-checkbox [(ngModel)]="doNotShowAgain">Do not show again</mat-checkbox>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-flat-button color="primary" (click)="confirm()">Continue Refresh</button>
    </mat-dialog-actions>
  `,
})
class CostRefreshWarningDialogComponent {
  /** Whether the user opted out of future warnings. Returned via `dialogRef.close()`. */
  doNotShowAgain = false;

  constructor(
    public readonly dialogRef: MatDialogRef<CostRefreshWarningDialogComponent>,
  ) { }

  /** Closes the dialog and passes the suppress-flag back to the caller. */
  confirm(): void {
    this.dialogRef.close(this.doNotShowAgain);
  }
}

/** Flat row rendered in the main cost table. */
interface CostRow {
  rowType: 'participant' | 'template';
  /** `true` when this participant is an ad-hoc account. */
  isAdhoc: boolean;
  /** Raw platform user ID — used for tooltip and export. */
  userId: string;
  /** Azure login ID from the resource ledger. */
  loginId: string;
  /** Resolved display name, falls back to userId when unavailable. */
  userName: string;
  resourceGroup: string;
  /** Raw Azure subscription GUID — used for tooltip and export. */
  subscriptionId: string;
  /** Resolved subscription display name, falls back to subscriptionId when unavailable. */
  subscriptionName: string;
  cost: number;
  currency: string;
  resources: ResourceCostItem[];
  /** Reconciliation outcome or ledger status (e.g. LiveAndHealthy, AllGone, Active). */
  status: string;
}

@Component({
  selector: 'app-cost-analysis-sub-tab',
  templateUrl: './cost-analysis-sub-tab.component.html',
  styleUrls: ['./cost-analysis-sub-tab.component.css'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
      state('expanded', style({ height: '*', visibility: 'visible' })),
      transition('expanded <=> collapsed', animate('200ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class CostAnalysisSubTabComponent implements OnChanges {
  @Input() classId!: string;
  @Input() classTitle = '';
  @Input() costReport: ClassVmCostReport | null = null;
  @Input() isLoading = false;
  @Input() loadError: string | null = null;
  @Input() exportService!: ExportService;

  @Output() loadRequested = new EventEmitter<void>();

  @ViewChild(MatPaginator) set paginator(p: MatPaginator) {
    if (p) this.mainDataSource.paginator = p;
  }

  readonly mainColumns = ['rowType', 'userName', 'resourceGroup', 'subscriptionName', 'status', 'cost'];
  readonly columnsWithExpand = [...this.mainColumns, 'expandedDetail'];
  readonly resourceColumns = ['name', 'resourceId', 'cost'];
  readonly pageSizeOptions = PAGINATOR_PAGE_SIZE_OPTIONS;
  readonly pageSize = DEFAULT_PAGINATOR_PAGE_SIZE;

  mainDataSource = new MatTableDataSource<CostRow>([]);
  resourceDataSource = new MatTableDataSource<ResourceCostItem>([]);
  selectedRow: CostRow | null = null;

  /** Counts consecutive Refresh clicks in the current component lifecycle. */
  private refreshCount = 0;

  /** localStorage key for the "do not show refresh warning" user preference. */
  private readonly QUIET_KEY = 'class:cost:user_vm:refresh_warn_quiet';

  constructor(
    private readonly dialog: MatDialog,
    private readonly classesEntityService: ClassEntityService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['costReport']) {
      this.rebuildMainTable();
      this.selectedRow = null;
      this.resourceDataSource.data = [];
    }
  }

  // ---------------------------------------------------------------------------
  // Derived properties
  // ---------------------------------------------------------------------------

  get hasData(): boolean {
    return this.costReport !== null && (
      (this.costReport.participants?.length ?? 0) > 0 || this.costReport.template !== null
    );
  }

  get formattedFromDate(): string {
    return this.costReport?.fromDate
      ? new Date(this.costReport.fromDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      : '—';
  }

  get formattedToDate(): string {
    return this.costReport?.toDate
      ? new Date(this.costReport.toDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      : '—';
  }

  formatCost(value: number | undefined | null): string {
    if (value == null || isNaN(value)) return '—';
    return `₹ ${value.toFixed(2)}`;
  }

  shortResourceId(resourceId: string): string {
    const parts = resourceId.split('/');
    return parts[parts.length - 1] ?? resourceId;
  }

  /**
   * Maps a raw reconciliation outcome or ledger status value to a human-readable
   * display label consistent with the User VMs tab power-state labels.
   * Falls back to the raw value for unrecognised statuses.
   */
  statusLabel(raw: string): string {
    const labels: Record<string, string> = {
      'LiveAndHealthy': 'Running',
      'Active': 'Active',
      'AllGone': 'Destroyed',
      'Deleted': 'Deleted',
      'ExternallyDeleted': 'Externally Deleted',
      'ComputeRecycled': 'VM Recycled',
      'PartialCleaned': 'Partially Cleaned',
      'PartiallyDeleted': 'Partially Deleted',
      'Superseded': 'Superseded',
    };
    return labels[raw] ?? raw;
  }

  /**
   * Maps a reconciliation or ledger status value to a CSS badge class.
   * Uses a lookup table (no if-else ladder) so new status values need
   * only a single entry here.
   */
  statusBadgeClass(status: string): string {
    const classMap: Record<string, string> = {
      'LiveAndHealthy': 'ca-badge-healthy',
      'Active': 'ca-badge-healthy',
      'AllGone': 'ca-badge-gone',
      'Deleted': 'ca-badge-gone',
      'ExternallyDeleted': 'ca-badge-gone',
      'ComputeRecycled': 'ca-badge-recycled',
      'PartialCleaned': 'ca-badge-recycled',
      'PartiallyDeleted': 'ca-badge-recycled',
    };
    return classMap[status] ?? 'ca-badge-unknown';
  }

  // ---------------------------------------------------------------------------
  // Table construction
  // ---------------------------------------------------------------------------

  private rebuildMainTable(): void {
    if (!this.costReport) {
      this.mainDataSource.data = [];
      return;
    }

    const rows: CostRow[] = [
      ...(this.costReport.participants ?? [])
        .map((p: ParticipantVmCost): CostRow => ({
          rowType: 'participant',
          isAdhoc: p.isAdhoc ?? false,
          userId: p.userId,
          loginId: p.loginId ?? '',
          userName: p.userName ?? p.userId,
          resourceGroup: p.resourceGroup,
          subscriptionId: p.subscriptionId,
          subscriptionName: p.subscriptionName ?? p.subscriptionId,
          cost: p.cost,
          currency: p.currency,
          resources: p.resources ?? [],
          status: p.reconciliationStatus ?? '—',
        }))
        .sort((a, b) => b.cost - a.cost),
    ];

    if (this.costReport.template) {
      const t: TemplateVmCost = this.costReport.template;
      rows.push({
        rowType: 'template',
        isAdhoc: false,
        userId: '— template —',
        loginId: '',
        userName: '— template —',
        resourceGroup: t.resourceGroup,
        subscriptionId: t.subscriptionId,
        subscriptionName: t.subscriptionName ?? t.subscriptionId,
        cost: t.cost,
        currency: t.currency,
        resources: t.resources ?? [],
        status: '—',
      });
    }

    this.mainDataSource.data = rows;
  }

  // ---------------------------------------------------------------------------
  // Row selection
  // ---------------------------------------------------------------------------

  selectRow(row: CostRow): void {
    if (this.selectedRow === row) {
      this.selectedRow = null;
      this.resourceDataSource.data = [];
    } else {
      this.selectedRow = row;
      this.resourceDataSource.data = row.resources;
    }
  }

  // ---------------------------------------------------------------------------
  // Refresh with warning
  // ---------------------------------------------------------------------------

  /**
   * Handles the Refresh/Load button click.
   * The initial Load click (before any data is present) does not count toward the
   * warning threshold — only explicit Refresh clicks on already-loaded data do.
   * On the 3rd such Refresh (and until the user suppresses the warning), shows a
   * dialog about Azure Cost Management's aggregation delay before proceeding.
   */
  onRefreshClick(): void {
    if (this.hasData) {
      this.refreshCount++;
    }
    const isQuiet = localStorage.getItem(this.QUIET_KEY) === 'true';
    if (this.refreshCount >= 3 && !isQuiet) {
      this.showRefreshWarning();
      return;
    }
    this.loadRequested.emit();
  }

  /**
   * Opens the refresh warning dialog. After close, persists the suppress preference
   * (if selected) and emits `loadRequested` so the actual data fetch proceeds.
   */
  private showRefreshWarning(): void {
    const ref = this.dialog.open(CostRefreshWarningDialogComponent, { width: '560px', minHeight: '300px' });
    ref.afterClosed().subscribe((doNotShow: boolean) => {
      if (doNotShow) {
        localStorage.setItem(this.QUIET_KEY, 'true');
      }
      this.loadRequested.emit();
    });
  }

  // ---------------------------------------------------------------------------
  // Excel export
  // ---------------------------------------------------------------------------

  /**
   * Exports the full VM cost report as a styled `.xlsx` file by calling the backend
   * ClosedXML export endpoint, then triggers a browser download of the returned blob.
   *
   * The backend handles all formatting (group header fills, sub-headers, currency formats,
   * column widths, and a totals row), so no SheetJS dependency is needed here.
   */
  exportExcel(): void {
    if (!this.costReport) return;

    const fileName =
      `${this.classTitle || this.classId}_vm_cost_report_${new Date().toISOString().slice(0, 10)}.xlsx`;

    this.classesEntityService.exportClassVmCostReport(this.classId).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        anchor.style.display = 'none';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
      },
      error: (err: unknown) => {
        console.error('Failed to export VM cost report:', err);
      },
    });
  }
}
